/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import pages from "../index";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import {
  ShieldCheck,
  Plus,
  Save,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Lock,
} from "lucide-react";
import { getEffectiveRoles } from "../../util/permissionUtils";

const isLockedRole = (key) => key === "dev" || key?.toLowerCase() === "ceo";

export const RolePermissionsManager = () => {
  const { toast } = useToast();
  const { callApi, configs, updateConfig } = Context();

  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState("admin");
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Modal: Add Custom Role
  const [isAddRoleOpen, setIsAddRoleOpen] = useState(false);
  const [newRoleKey, setNewRoleKey] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [newRoleColor, setNewRoleColor] = useState("blue");

  // Load custom roles and role permissions from configs
  const customRoles = useMemo(() => {
    return Array.isArray(configs?.customRoles) ? configs.customRoles : [];
  }, [configs?.customRoles]);

  const allRoles = useMemo(() => {
    return getEffectiveRoles(configs);
  }, [configs]);

  // Initial state for rolePermissions matrix: { [roleKey]: [paths...] }
  const [rolePermissions, setRolePermissions] = useState({});

  // Flatten all distinct pages and subpages with category name
  const allMenuPages = useMemo(() => {
    const list = [];
    pages.forEach((page) => {
      if (page.hideFromPermission || page.path === "/login" || page.path === "/logout") {
        return;
      }

      const mainItem = {
        path: page.path,
        name: page.name,
        category: page.name,
        isParent: true,
        hasChildren: Boolean(page.children && page.children.length > 0),
        defaultPermissions: page.permissions || [],
      };
      list.push(mainItem);

      if (page.children && page.children.length > 0) {
        page.children.forEach((child) => {
          if (child.hideFromPermission) return;
          list.push({
            path: child.path,
            name: child.name,
            category: page.name,
            isParent: false,
            parentPath: page.path,
            defaultPermissions: child.permissions || page.permissions || [],
          });
        });
      }
    });
    return list;
  }, []);

  // Initialize role permissions from config or fallback to pages default
  useEffect(() => {
    const configPerms = configs?.rolePermissions || {};
    const initialPerms = {};

    allRoles.forEach((role) => {
      if (Array.isArray(configPerms[role.key])) {
        initialPerms[role.key] = [...configPerms[role.key]];
      } else {
        // Fallback to default permissions defined in pages
        const defaultAllowed = allMenuPages
          .filter((p) => {
            if (role.key === "dev" || role.key.toLowerCase() === "ceo") return true;
            return p.defaultPermissions.includes(role.key);
          })
          .map((p) => p.path);
        initialPerms[role.key] = defaultAllowed;
      }
    });

    setRolePermissions(initialPerms);
  }, [configs?.rolePermissions, allRoles, allMenuPages]);

  // Check if role has access to specific path
  const hasAccess = (roleKey, path) => {
    if (isLockedRole(roleKey)) return true; // dev and ceo always have access
    return (rolePermissions[roleKey] || []).includes(path);
  };

  // Toggle single permission
  const handleTogglePath = (roleKey, path) => {
    if (isLockedRole(roleKey)) return; // cannot modify dev or ceo

    setRolePermissions((prev) => {
      const currentList = prev[roleKey] || [];
      const isCurrentlyAllowed = currentList.includes(path);

      let updatedList;
      if (isCurrentlyAllowed) {
        updatedList = currentList.filter((p) => p !== path);
      } else {
        updatedList = [...currentList, path];
      }

      return {
        ...prev,
        [roleKey]: updatedList,
      };
    });
  };

  // Bulk toggle for a category
  const handleToggleCategory = (roleKey, categoryName, shouldSelectAll) => {
    if (isLockedRole(roleKey)) return;

    const categoryPaths = allMenuPages
      .filter((p) => p.category === categoryName)
      .map((p) => p.path);

    setRolePermissions((prev) => {
      const currentList = prev[roleKey] || [];
      let updatedList;

      if (shouldSelectAll) {
        updatedList = Array.from(new Set([...currentList, ...categoryPaths]));
      } else {
        updatedList = currentList.filter((p) => !categoryPaths.includes(p));
      }

      return {
        ...prev,
        [roleKey]: updatedList,
      };
    });
  };

  // Select all / Deselect all for current role
  const handleSelectAllForRole = (roleKey, selectAll) => {
    if (isLockedRole(roleKey)) return;

    setRolePermissions((prev) => ({
      ...prev,
      [roleKey]: selectAll ? allMenuPages.map((p) => p.path) : [],
    }));
  };

  // Reset to default
  const handleResetToDefault = (roleKey) => {
    if (isLockedRole(roleKey)) return;
    if (!window.confirm(`ต้องการรีเซ็ตสิทธิ์ของ "${roleKey}" กลับเป็นค่าเริ่มต้นใช่หรือไม่?`)) {
      return;
    }

    const defaultAllowed = allMenuPages
      .filter((p) => p.defaultPermissions.includes(roleKey))
      .map((p) => p.path);

    setRolePermissions((prev) => ({
      ...prev,
      [roleKey]: defaultAllowed,
    }));

    toast({
      className: "bg-blue-600 text-white rounded-md",
      title: (
        <div className="flex items-center gap-2 font-semibold text-sm">
          <CheckCircle2 className="w-4 h-4" /> รีเซ็ตสิทธิ์เริ่มต้นเรียบร้อย (อย่าลืมกดบันทึก)
        </div>
      ),
    });
  };

  // Save changes to database
  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const res = await callApi("configs-edit", {
        id: "general",
        key: "rolePermissions",
        value: rolePermissions,
      });

      if (res?.status || res?.success) {
        toast({
          className: "bg-emerald-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <CheckCircle2 className="w-4 h-4" /> บันทึกการตั้งค่าสิทธิ์เรียบร้อยแล้ว
            </div>
          ),
        });
        await updateConfig();
      } else {
        toast({
          className: "bg-rose-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <XCircle className="w-4 h-4" /> บันทึกไม่สำเร็จ: {res?.message || "เกิดข้อผิดพลาด"}
            </div>
          ),
        });
      }
    } catch (err) {
      console.warn("Save permissions error:", err);
    } finally {
      setSaving(false);
    }
  };

  // Add custom role
  const handleAddRole = async (e) => {
    e.preventDefault();
    const cleanKey = newRoleKey.trim().replace(/\s+/g, "_");
    const cleanName = newRoleName.trim();

    if (!cleanKey || !cleanName) {
      alert("กรุณากรอกรหัสบทบาทและชื่อบทบาท");
      return;
    }

    if (allRoles.some((r) => r.key.toLowerCase() === cleanKey.toLowerCase())) {
      alert("รหัสบทบาทนี้มีอยู่ในระบบแล้ว กรุณาใช้รหัสอื่น");
      return;
    }

    const colorMap = {
      blue: { color: "bg-blue-50 text-blue-700 border-blue-200", badgeColor: "bg-blue-500 text-white" },
      emerald: { color: "bg-emerald-50 text-emerald-800 border-emerald-200", badgeColor: "bg-emerald-500 text-white" },
      amber: { color: "bg-amber-50 text-amber-800 border-amber-200", badgeColor: "bg-amber-500 text-white" },
      purple: { color: "bg-purple-50 text-purple-700 border-purple-200", badgeColor: "bg-purple-500 text-white" },
      rose: { color: "bg-rose-50 text-rose-700 border-rose-200", badgeColor: "bg-rose-500 text-white" },
      indigo: { color: "bg-indigo-50 text-indigo-800 border-indigo-200", badgeColor: "bg-indigo-500 text-white" },
      slate: { color: "bg-slate-50 text-slate-700 border-slate-200", badgeColor: "bg-slate-600 text-white" },
    };

    const selectedStyle = colorMap[newRoleColor] || colorMap.slate;

    const newRoleObj = {
      key: cleanKey,
      name: cleanName,
      desc: newRoleDesc.trim() || cleanName,
      color: selectedStyle.color,
      badgeColor: selectedStyle.badgeColor,
      isCustom: true,
    };

    const updatedCustomRoles = [...customRoles, newRoleObj];

    try {
      const res = await callApi("configs-edit", {
        id: "general",
        key: "customRoles",
        value: updatedCustomRoles,
      });

      if (res?.status || res?.success) {
        toast({
          className: "bg-emerald-600 text-white rounded-md",
          title: (
            <div className="flex items-center gap-2 font-semibold text-sm">
              <CheckCircle2 className="w-4 h-4" /> เพิ่มบทบาท "{cleanName}" สำเร็จ
            </div>
          ),
        });
        await updateConfig();
        setIsAddRoleOpen(false);
        setNewRoleKey("");
        setNewRoleName("");
        setNewRoleDesc("");
        setSelectedRole(cleanKey);
      }
    } catch (err) {
      console.warn("Add role error:", err);
    }
  };

  // Group pages by category
  const categorizedPages = useMemo(() => {
    const groups = {};
    allMenuPages.forEach((p) => {
      if (!groups[p.category]) {
        groups[p.category] = [];
      }
      groups[p.category].push(p);
    });
    return groups;
  }, [allMenuPages]);

  const activeRoleObj = allRoles.find((r) => r.key === selectedRole) || allRoles[0];

  return (
    <div className="space-y-4">
      {/* Top Banner & Action Controls */}
      <div className="bg-white border border-slate-200 rounded-md p-4 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            ตารางกำหนดสิทธิ์การเข้าถึงเมนู (Role & Permission Matrix)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            เลือกตำแหน่งที่ต้องการปรับแต่งสิทธิ์ และติ๊ก Checkbox เมนูที่อนุญาตให้มองเห็นและเข้าถึงได้
          </p>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setIsAddRoleOpen(true)}
            className="flex-1 sm:flex-initial justify-center flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-md text-xs font-bold transition-colors border border-slate-300 shadow-xs cursor-pointer min-h-[38px] sm:min-h-auto"
          >
            <Plus className="w-4 h-4 text-emerald-600" />
            <span>+ เพิ่มบทบาทใหม่</span>
          </button>

          <button
            type="button"
            onClick={handleSaveChanges}
            disabled={saving}
            className="flex-1 sm:flex-initial justify-center flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold transition-all shadow-sm disabled:opacity-60 cursor-pointer min-h-[38px] sm:min-h-auto"
          >
            <Save className={`w-4 h-4 ${saving ? "animate-spin" : ""}`} />
            <span>{saving ? "กำลังบันทึก..." : "บันทึกข้อมูล"}</span>
          </button>
        </div>
      </div>

      {/* Role Selection Tabs / Badges */}
      <div className="bg-white border border-slate-200 rounded-md p-3 shadow-sm">
        <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">
          เลือกตำแหน่งที่ต้องการตั้งค่า (Select Role):
        </p>
        <div className="flex flex-wrap gap-2">
          {allRoles.map((role) => {
            const isSelected = selectedRole === role.key;
            const allowedCount = (rolePermissions[role.key] || []).length;

            return (
              <button
                key={role.key}
                type="button"
                onClick={() => setSelectedRole(role.key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold transition-all border cursor-pointer min-h-[36px] ${
                  isSelected
                    ? `${role.color} ring-2 ring-emerald-500 font-bold shadow-xs`
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                }`}
              >
                <span>{role.name}</span>
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                    isSelected ? "bg-white/80 text-slate-800" : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {isLockedRole(role.key) ? "เข้าได้ทุกหน้า (ล็อกสิทธิ์)" : `${allowedCount} หน้า`}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Role Permission Detail Card */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        {/* Role Header Info */}
        <div className="p-3 sm:p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-md text-xs font-bold border ${activeRoleObj.color}`}>
              {activeRoleObj.name} ({activeRoleObj.key})
            </span>
            <span className="text-xs text-slate-500 font-medium">{activeRoleObj.desc}</span>
          </div>

          {isLockedRole(selectedRole) ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 text-slate-500 text-xs font-semibold">
              <Lock className="w-3.5 h-3.5 text-slate-400" />
              <span>ล็อกสิทธิ์เข้าถึงทุกหน้า ({selectedRole === "dev" ? "Dev" : "CEO"})</span>
            </span>
          ) : (
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              <button
                type="button"
                onClick={() => handleSelectAllForRole(selectedRole, true)}
                className="flex-1 sm:flex-initial px-2.5 py-1.5 sm:py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-md text-xs font-semibold transition-colors text-center min-h-[34px] sm:min-h-auto"
              >
                เลือกทั้งหมด
              </button>
              <button
                type="button"
                onClick={() => handleSelectAllForRole(selectedRole, false)}
                className="flex-1 sm:flex-initial px-2.5 py-1.5 sm:py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-md text-xs font-semibold transition-colors text-center min-h-[34px] sm:min-h-auto"
              >
                ยกเลิกทั้งหมด
              </button>
              <button
                type="button"
                onClick={() => handleResetToDefault(selectedRole)}
                className="flex-1 sm:flex-initial flex items-center justify-center gap-1 px-2.5 py-1.5 sm:py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-md text-xs font-semibold transition-colors min-h-[34px] sm:min-h-auto"
                title="รีเซ็ตกลับเป็นค่าเริ่มต้นเดิม"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>ค่าเริ่มต้น</span>
              </button>
            </div>
          )}
        </div>

        {/* Categories & Pages Permission List */}
        <div className="divide-y divide-slate-100 p-2">
          {Object.entries(categorizedPages).map(([categoryName, pagesList]) => {
            const isCategoryCollapsed = Boolean(collapsedCategories[categoryName]);
            const allowedInCategory = pagesList.filter((p) => hasAccess(selectedRole, p.path)).length;
            const isAllInCategoryAllowed = allowedInCategory === pagesList.length;

            return (
              <div key={categoryName} className="py-2">
                {/* Category Header */}
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50/80 rounded-md mb-1.5 border border-slate-200/60">
                  <div
                    className="flex items-center gap-2 cursor-pointer flex-1"
                    onClick={() =>
                      setCollapsedCategories((prev) => ({
                        ...prev,
                        [categoryName]: !prev[categoryName],
                      }))
                    }
                  >
                    {isCategoryCollapsed ? (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="font-bold text-xs text-slate-800">{categoryName}</span>
                    <span className="text-[11px] text-slate-500">
                      ({allowedInCategory}/{pagesList.length} หน้า)
                    </span>
                  </div>

                  {!isLockedRole(selectedRole) && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          handleToggleCategory(selectedRole, categoryName, !isAllInCategoryAllowed)
                        }
                        className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200 transition-colors"
                      >
                        {isAllInCategoryAllowed ? "ปิดทั้งหมวด" : "เปิดทั้งหมวด"}
                      </button>
                    </div>
                  )}
                </div>

                {/* Sub Pages Checkboxes */}
                {!isCategoryCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 px-3 py-1">
                    {pagesList.map((pageItem) => {
                      const allowed = hasAccess(selectedRole, pageItem.path);

                      return (
                        <label
                          key={pageItem.path}
                          className={`flex items-start gap-2.5 p-2 rounded-md border transition-all text-xs select-none ${
                            allowed
                              ? "bg-emerald-50/60 border-emerald-300 text-slate-900 font-semibold"
                              : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                          } ${isLockedRole(selectedRole) ? "cursor-default opacity-85" : "cursor-pointer"}`}
                        >
                          <input
                            type="checkbox"
                            disabled={isLockedRole(selectedRole)}
                            checked={allowed}
                            onChange={() => handleTogglePath(selectedRole, pageItem.path)}
                            className="mt-0.5 rounded text-emerald-600 focus:ring-emerald-500 w-4 h-4 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-semibold">{pageItem.name}</p>
                            <p className="text-[10px] text-slate-400 font-mono truncate">
                              {pageItem.path}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* MODAL: ADD CUSTOM ROLE */}
      {isAddRoleOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md max-w-md w-full p-4 sm:p-5 border border-slate-200 shadow-xl space-y-4 animate-in fade-in-50 duration-150 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h4 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                เพิ่มบทบาท / ตำแหน่งใหม่ (Add Role)
              </h4>
              <button
                type="button"
                onClick={() => setIsAddRoleOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-sm font-bold p-1"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddRole} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  รหัสบทบาท (Role Key - ภาษาอังกฤษ เช่น cs_trainee, auditor) *
                </label>
                <input
                  type="text"
                  required
                  value={newRoleKey}
                  onChange={(e) => setNewRoleKey(e.target.value)}
                  placeholder="เช่น qa_tester, customer_lead"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs font-mono focus:outline-none focus:bg-white focus:border-emerald-500 min-h-[38px]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  ชื่อตำแหน่งที่แสดง (Role Name - ภาษาไทยหรืออังกฤษ) *
                </label>
                <input
                  type="text"
                  required
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="เช่น เจ้าหน้าที่ตรวจสอบคุณภาพ, ผู้ประสานงาน"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs font-semibold focus:outline-none focus:bg-white focus:border-emerald-500 min-h-[38px]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  คำอธิบายหน้าที่โดยสังเขป
                </label>
                <input
                  type="text"
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                  placeholder="เช่น ดูแลเฉพาะข้อมูลคำสั่งซื้อและตรวจสลาก"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-xs focus:outline-none focus:bg-white focus:border-emerald-500 min-h-[38px]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  โทนสีป้ายประจำตำแหน่ง (Color Theme)
                </label>
                <div className="flex gap-2 flex-wrap">
                  {["blue", "emerald", "amber", "purple", "rose", "indigo", "slate"].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewRoleColor(c)}
                      className={`w-7 h-7 rounded-md border-2 transition-transform ${
                        newRoleColor === c ? "scale-110 border-slate-900 shadow-xs" : "border-transparent"
                      } ${
                        c === "blue"
                          ? "bg-blue-500"
                          : c === "emerald"
                          ? "bg-emerald-500"
                          : c === "amber"
                          ? "bg-amber-500"
                          : c === "purple"
                          ? "bg-purple-500"
                          : c === "rose"
                          ? "bg-rose-500"
                          : c === "indigo"
                          ? "bg-indigo-500"
                          : "bg-slate-600"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsAddRoleOpen(false)}
                  className="flex-1 sm:flex-initial px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-semibold transition-colors min-h-[38px]"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  className="flex-1 sm:flex-initial px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-bold transition-colors shadow-sm min-h-[38px]"
                >
                  ยืนยันเพิ่มบทบาท
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
